import type { Addresses } from 'api/types/addresses.types';
import { LightPlusIcon } from 'assets/icons/app';
import type { OptionsProps } from 'components/molecules/OptionItem';
import OptionItem from 'components/molecules/OptionItem';
import { AdapterAddressModule } from 'modes/adapters';
import { generateAddressValue } from 'modes/standalone/addressModule/convertCareemAddress';
import { selectCurrentOrder } from 'pages/newOrder/newOrder.selectors';
import { useGetAllCustomerAddresses } from 'pages/queries/address.queries';
import type { FC } from 'react';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';

import AddressSheetContextProvider from '../../provider/AddressSheetContextProvider';

export const AddressSheetContext = createContext({ toggleSheet: () => {} });
export const useAddressSheet = () => useContext(AddressSheetContext);

const StyledOptionItem = styled(OptionItem)`
  padding-top: 11px;
  padding-bottom: 11px;
`;

interface ISelectAddressProps {
  hasRightArrowOnly?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  showAddressSheet?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  toggleAddressSheet?: () => void;
  // eslint-disable-next-line react/no-unused-prop-types
  optionProps?: Omit<OptionsProps, 'title'>;
}

export const AddressComponents: FC<ISelectAddressProps> = ({ hasRightArrowOnly = false, showAddressSheet, toggleAddressSheet, optionProps }) => {
  const { t } = useTranslation();
  const { addressId } = useSelector(selectCurrentOrder);
  const theme = useTheme();
  const { data: addresses, isLoading: isAddressLoading } = useGetAllCustomerAddresses({
    enabled: Boolean(!addressId),
  });

  const selectedAddress = useMemo(() => addresses?.find((address: Addresses.Address) => address.id === addressId), [addressId, addresses]);

  const handleOptionClick = useCallback(() => {
    toggleAddressSheet?.();
  }, [toggleAddressSheet]);

  const address = useMemo(() => generateAddressValue(selectedAddress), [selectedAddress]);

  const isShowRightButtonOnUnselected = useMemo(
    () => theme.components.selectPickupOrDropOffItem?.showUnselectedRightButton && !address,
    [theme.components.selectPickupOrDropOffItem?.showUnselectedRightButton, address]
  );

  const rightIcon = useMemo(
    () => (isShowRightButtonOnUnselected ? <LightPlusIcon /> : undefined),
    [theme.components.selectPickupOrDropOffItem?.showUnselectedRightButton, address]
  );

  const onClose = useCallback(() => toggleAddressSheet?.(), [toggleAddressSheet]);
  const onSubmit = useCallback(() => toggleAddressSheet?.(), [toggleAddressSheet]);
  const textCode = selectedAddress?.addressText ? 'common.address' : 'common.addAddress';

  return (
    <>
      <StyledOptionItem
        onClick={handleOptionClick}
        startIconName="icon-address"
        hideItemSeparator
        title={t(textCode)}
        actionType={hasRightArrowOnly ? 'open' : 'edit'}
        hasRightArrowOnly={hasRightArrowOnly || isShowRightButtonOnUnselected}
        rightIcon={rightIcon}
        value={address}
        {...optionProps}
      />
      <AdapterAddressModule show={showAddressSheet!!} onClose={onClose} onSubmit={onSubmit} isLoading={isAddressLoading} />
    </>
  );
};

const SelectAddress: FC<ISelectAddressProps> = ({ hasRightArrowOnly = false }) => {
  const [showAddressSheet, setShowAddressSheet] = useState(false);
  const toggleAddressSheet = () => setShowAddressSheet(!showAddressSheet);

  return (
    <AddressSheetContextProvider toggleSheet={toggleAddressSheet}>
      <AddressComponents hasRightArrowOnly={hasRightArrowOnly} toggleAddressSheet={toggleAddressSheet} showAddressSheet={showAddressSheet} />
    </AddressSheetContextProvider>
  );
};

export default SelectAddress;
